/**
 * <s-variant-media> — Moves the product gallery to the selected variant's featured media.
 *
 * Rendered inside the "product-form" partial. After a variant change the partial is either
 * replaced (a fresh element connects) or morphed in place (data-media-id changes), so both
 * connectedCallback and attributeChangedCallback scroll the gallery track to the slide whose
 * data-media-id matches.
 *
 * Required markup:
 *   <s-variant-media data-media-id="…" hidden> inside [data-product-detail]
 *   [data-product-gallery] track inside the same [data-product-detail]
 *   [data-media-id] on each gallery slide
 *
 * On the first page load it only moves the gallery when the URL deep-links a variant
 * (?variant=), so the product's main image still leads by default.
 */
class VariantMedia extends HTMLElement {
  static get observedAttributes() {
    return ['data-media-id'];
  }

  connectedCallback() {
    this.#show();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.#show();
  }

  #show() {
    const mediaId = this.dataset.mediaId;
    const detail = this.closest('[data-product-detail]');
    if (!mediaId || !detail) return;

    const isInitialLoad = !detail.hasAttribute('data-variant-media-ready');
    detail.setAttribute('data-variant-media-ready', '');
    if (isInitialLoad && !new URL(location.href).searchParams.has('variant')) return;

    const track = detail.querySelector('[data-product-gallery]');
    const slide = track && Array.from(track.querySelectorAll('[data-media-id]'))
      .find((element) => element.dataset.mediaId === mediaId);
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Wait a frame so any in-flight view transition has painted the new layout.
    requestAnimationFrame(() => {
      const offset = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
      track.scrollTo({
        left: track.scrollLeft + offset,
        behavior: isInitialLoad || reduceMotion ? 'instant' : 'smooth',
      });
    });
  }
}

if (!customElements.get('s-variant-media')) {
  customElements.define('s-variant-media', VariantMedia);
}
